import React, { useState, useCallback, useEffect } from 'react';
import '../styles/SortableResizableTable.css';

// Component for resizable table columns with sorting capabilities
const SortableResizableTable = ({
  columns,
  data,
  customRender = {},
  hiddenPredicate = null,
  expandableRows = false,
  expandedRows: parentExpandedRows = null, // External control of expanded rows
  onRowExpand = null,
  renderExpandedContent = null,
  columnClickHandlers = {},
  tradeFormData = {},
  onTradeFormChange = null,
  maxRows = null, // Optional row limit for tables like CC Management
  tableClassName = '', // Optional CSS class for specific table styling
  generateRowId = null, // Function to generate consistent row IDs
  onRowClick = null, // Function called when row is clicked (alternative to expandableRows)
  emptyMessage = 'No data available' // Message to show when data is empty
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' }); const [sortedData, setSortedData] = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilterDropdowns, setShowFilterDropdowns] = useState({});
  const [internalExpandedRows, setInternalExpandedRows] = useState(new Set());
  // Use external expanded rows if provided, otherwise use internal state
  const expandedRows = parentExpandedRows !== null ? parentExpandedRows : internalExpandedRows;

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-container')) {
        setShowFilterDropdowns({});
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  // Determine which columns are available in the data
  useEffect(() => {
    if (data && data.length > 0) {
      // Get all possible fields from the first row of data
      const sampleRow = data[0];
      const availableKeys = Object.keys(sampleRow);

      // Helper function to find a matching key with flexible matching
      const findMatchingKey = (targetKey) => {

        // Direct match
        if (availableKeys.includes(targetKey)) {
          return targetKey;
        }

        const lowerTargetKey = targetKey.toLowerCase();
        const normalizedTargetKey = lowerTargetKey.replace(/[_\s-]/g, '');

        // Case-insensitive match
        for (const key of availableKeys) {
          const lowerKey = key.toLowerCase();
          const normalizedKey = lowerKey.replace(/[_\s-]/g, '');

          // Exact match after normalization
          if (normalizedKey === normalizedTargetKey) {
            return key;
          }

          // Partial match for common variations
          if (
            (targetKey === 'accountName' && key.includes('Account') && key.includes('Name')) ||
            (targetKey === 'holdingsCurrentValue' && key.includes('Holdings') && key.includes('Value')) ||
            (targetKey === 'sleeveName' && key.includes('Sleeve') && key.includes('Name')) ||
            (targetKey === 'targetWeight' && key.includes('Target') && key.includes('Weight') && !key.includes('Minimum') && !key.includes('Maximum')) ||
            (targetKey === 'minimumTargetWeight' && key.includes('Minimum') && key.includes('Target') && key.includes('Weight')) ||
            (targetKey === 'maximumTargetWeight' && key.includes('Maximum') && key.includes('Target') && key.includes('Weight')) ||
            (targetKey === 'actualWeight' && key.includes('Actual') && key.includes('Weight') && !key.includes('Assigned')) ||
            (targetKey === 'actualWeightOfAssigned' && key.includes('Actual') && key.includes('Weight') && key.includes('Assigned')) ||
            (targetKey === 'securityLevelModel' && (key.includes('Security') || key.includes('Model'))) ||
            (targetKey === 'Trade Action' && key.includes('Trade') && key.includes('Action')) ||
            (targetKey === 'Cash Action' && key.includes('Cash') && key.includes('Action'))
          ) {
            return key;
          }
        }

        // No match found
        // console.log(`No match found for: ${targetKey}`);
        return null;
      };
      // Filter and map columns to use the correct data keys
      const filteredColumns = columns
        .map(col => {
          const matchingKey = findMatchingKey(col.key);
          // Allow columns without data keys (synthetic columns like Actions)
          return matchingKey ? { ...col, dataKey: matchingKey } : { ...col, dataKey: null };
        });
      setAvailableColumns(filteredColumns.length > 0 ? filteredColumns : columns);
    } else {
      setAvailableColumns(columns);
    }
  }, [data, columns]);

  // Initial column widths
  useEffect(() => {
    const initialWidths = {};
    availableColumns.forEach((col) => {
      initialWidths[col.key] = col.width || col.initialWidth || 150; // Default width
    });
    setColumnWidths(initialWidths);
  }, [availableColumns]);
  // Double-click a resize handle to auto-size the column (simple implementation)
  const handleDoubleClick = useCallback((columnKey) => {
    // Set a reasonable width based on column type
    // For real auto-sizing, you would measure content,
    // but this simple approach provides a useful reset functionality
    const defaultWidths = {
      // Columns that typically contain numbers can be narrower
      targetWeight: 120,
      minimumTargetWeight: 160,
      maximumTargetWeight: 160,
      actualWeight: 120,
      actualWeightOfAssigned: 200,
      // Text columns should be wider
      sleeveName: 180,
      accountNames: 250,
      // Currency columns somewhere in between
      holdingsCurrentValue: 170,
    };

    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: defaultWidths[columnKey] || 150
    }));
  }, []);// Sort the data whenever sortConfig, data, or columnFilters changes
  useEffect(() => {
    // Apply column filters to data
    const applyColumnFilters = (dataToFilter) => {
      return dataToFilter.filter(item => {
        return Object.entries(columnFilters).every(([columnKey, filterValue]) => {
          if (!filterValue) return true;

          const dataKey = availableColumns.find(col => col.key === columnKey)?.dataKey || columnKey;
          const cellValue = item[dataKey] !== undefined ? item[dataKey] : item[columnKey];

          if (cellValue === null || cellValue === undefined) return false;

          return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
        });
      });
    };

    // First apply column filters
    let sortableData = applyColumnFilters([...data]);

    if (sortConfig.key !== null) {
      // Find the column definition to get the dataKey if it exists
      const column = availableColumns.find(col => col.key === sortConfig.key);
      const dataKey = column?.dataKey || sortConfig.key;

      // Comparator function used for both visible and hidden subsets
      const comparator = (a, b) => {
        // Special handling for actual weight columns - sort by difference from target
        if ((column.key === 'Actual Weight' || column.key === 'Actual Weight (of Assigned)') &&
          ((a['Target Weight'] !== undefined && b['Target Weight'] !== undefined))) {

          let aDifference, bDifference;
          let aTarget = a['Target Weight'];
          let bTarget = b['Target Weight'];

          if (column.key === 'Actual Weight') {
            const aActual = a[dataKey] !== undefined ? a[dataKey] : a['Actual Weight'];
            const bActual = b[dataKey] !== undefined ? b[dataKey] : b['Actual Weight'];
            aDifference = Math.abs(aTarget - aActual);
            bDifference = Math.abs(bTarget - bActual);
          }
          else if (column.key === 'Actual Weight (of Assigned)') {
            const aActualOfAssigned = a[dataKey] !== undefined ? a[dataKey] : a['Actual Weight (of Assigned)'];
            const bActualOfAssigned = b[dataKey] !== undefined ? b[dataKey] : b['Actual Weight (of Assigned)'];
            aDifference = Math.abs(aTarget - aActualOfAssigned);
            bDifference = Math.abs(bTarget - bActualOfAssigned);
          }

          if (aDifference !== bDifference) {
            return sortConfig.direction === 'asc' ? aDifference - bDifference : bDifference - aDifference;
          }

          const aValue = a[dataKey] !== undefined ? a[dataKey] : a[sortConfig.key];
          const bValue = b[dataKey] !== undefined ? b[dataKey] : b[sortConfig.key];

          if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        }

        // Standard sorting for all other columns
        const aValue = a[dataKey] !== undefined ? a[dataKey] : a[sortConfig.key];
        const bValue = b[dataKey] !== undefined ? b[dataKey] : b[sortConfig.key];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
        if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      };

      if (typeof hiddenPredicate === 'function') {
        const visible = sortableData.filter(item => !hiddenPredicate(item));
        const hidden = sortableData.filter(item => hiddenPredicate(item));
        visible.sort(comparator);
        hidden.sort(comparator);
        sortableData = [...visible, ...hidden];
      } else {
        sortableData.sort(comparator);
      }
    }
    setSortedData(sortableData);
  }, [sortConfig, data, availableColumns, columnFilters, hiddenPredicate]);
  // Get unique values for a column (for filter options)
  const getUniqueColumnValues = useCallback((columnKey) => {
    const dataKey = availableColumns.find(col => col.key === columnKey)?.dataKey || columnKey;
    const values = data.map(item => {
      const value = item[dataKey] !== undefined ? item[dataKey] : item[columnKey];
      return value;
    }).filter(value => value !== null && value !== undefined);

    return [...new Set(values)].sort();
  }, [data, availableColumns]);
  // Handle column filter change
  const handleColumnFilterChange = useCallback((columnKey, filterValue) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnKey]: filterValue
    }));
  }, []);

  // Toggle filter dropdown visibility
  const toggleFilterDropdown = useCallback((columnKey) => {
    setShowFilterDropdowns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  }, []);

  // Clear specific column filter
  const clearColumnFilter = useCallback((columnKey) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnKey];
      return newFilters;
    });
  }, []);

  // Toggle row expansion - use stable identifier instead of index
  const toggleRowExpansion = useCallback((item, originalIndex) => {
    // Create a stable identifier for the row using the provided function or fallback
    const rowId = generateRowId 
      ? generateRowId(item, originalIndex)
      : (item.Symbol || item.symbol || item.id || `row-${originalIndex}`);

    if (parentExpandedRows !== null) {
      // Parent controls expanded rows, just call the callback
      if (onRowExpand) {
        const isCurrentlyExpanded = parentExpandedRows.has(rowId);
        onRowExpand(item, originalIndex, !isCurrentlyExpanded, rowId);
      }
    } else {
      // Internal control of expanded rows
      setInternalExpandedRows(prev => {
        const newSet = new Set(prev);
        if (newSet.has(rowId)) {
          newSet.delete(rowId);
        } else {
          newSet.add(rowId);
        }
        return newSet;
      });

      // Call optional onRowExpand callback
      if (onRowExpand) {
        const isCurrentlyExpanded = expandedRows.has(rowId);
        onRowExpand(item, originalIndex, !isCurrentlyExpanded, rowId);
      }
    }
  }, [onRowExpand, expandedRows, parentExpandedRows, generateRowId]);
  // Request a sort by column key
  const requestSort = useCallback((key) => {
    let direction = 'asc';

    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key, direction });
  }, [sortConfig.key, sortConfig.direction]);

  // Start resizing a column
  const handleMouseDown = (e, columnKey) => {
    e.preventDefault();
    setResizingColumn(columnKey);
    setStartX(e.clientX);
    setStartWidth(columnWidths[columnKey]);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  // Resize the column based on mouse movement
  const handleMouseMove = useCallback((e) => {
    if (resizingColumn) {
      const width = startWidth + (e.clientX - startX);
      setColumnWidths(prev => ({
        ...prev,
        [resizingColumn]: Math.max(50, width) // Min width: 50px
      }));
    }
  }, [resizingColumn, startWidth, startX]);

  // Stop resizing when mouse is released
  const handleMouseUp = useCallback(() => {
    setResizingColumn(null);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  // Clean up event listeners when component unmounts
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Calculate total table width from column widths
  const totalTableWidth = availableColumns.reduce((sum, col) => sum + (columnWidths[col.key] || 150), 0);

  // Render the sortable, resizable table with scrollable body
  return (
    <div className={`sortable-resizable-table-container table-with-scroll ${tableClassName}`}>
      {/* Fixed Header */}
      <div className="table-header-wrapper">
        <table className="sortable-resizable-table table table-striped table-hover" style={{ width: totalTableWidth, minWidth: '100%' }}>
          <colgroup>
            {availableColumns.map((column) => (
              <col
                key={column.key}
                style={{ width: `${columnWidths[column.key]}px` }}
              />
            ))}
          </colgroup>
          <thead className="table-light">
            <tr>
              {availableColumns.map((column) => (
                <th
                  key={column.key}
                  style={{ position: 'relative' }}
                  onClick={column.sortable ? () => requestSort(column.key) : undefined}
                  className={`${column.sortable ? 'sortable' : ''} ${resizingColumn === column.key ? 'resizing' : ''}`}
                  title={column.description || column.label} // Show tooltip with description or label
                >
                  <div className="th-content">
                    {column.label}
                    {sortConfig.key === column.key && (
                      <span className={`sort-icon ${sortConfig.direction === 'asc' ? 'asc' : 'desc'}`}>
                        {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    )}
                    {/* Add filter button for filterable columns */}
                    {column.filterable && (
                      <div className="filter-container">
                        <button
                          className={`filter-btn ${columnFilters[column.key] ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFilterDropdown(column.key);
                          }}
                          title={columnFilters[column.key] ? `Filtered by: ${columnFilters[column.key]}` : "Filter this column"}
                        >
                          {columnFilters[column.key] ? '🔍✓' : '🔍'}
                        </button>
                        {showFilterDropdowns[column.key] && (
                          <div className="filter-dropdown">
                            <div className="filter-dropdown-header">
                              <span>Filter {column.label}</span>
                              <button
                                className="filter-close-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleFilterDropdown(column.key);
                                }}
                              >
                                ×
                              </button>
                            </div>
                            <div className="filter-options">
                              <input
                                type="text"
                                className="filter-search"
                                placeholder="Search..."
                                value={columnFilters[column.key] || ''}
                                onChange={(e) => handleColumnFilterChange(column.key, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              {columnFilters[column.key] && (
                                <button
                                  className="filter-clear-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    clearColumnFilter(column.key);
                                  }}
                                >
                                  Clear
                                </button>
                              )}
                              <div className="filter-values">
                                {getUniqueColumnValues(column.key).slice(0, 10).map((value, index) => (
                                  <div
                                    key={index}
                                    className="filter-value"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleColumnFilterChange(column.key, String(value));
                                    }}
                                  >
                                    {String(value)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div
                    className="resize-handle"
                    onMouseDown={(e) => handleMouseDown(e, column.key)}
                    onDoubleClick={() => handleDoubleClick(column.key)}
                    title="Drag to resize column. Double-click to reset width."
                  />
                </th>
              ))}

            </tr>
          </thead>
        </table>
      </div>
      {/* Scrollable Body */}
      <div className="table-body-wrapper">
        <table className="sortable-resizable-table table table-striped table-hover" style={{ width: totalTableWidth, minWidth: '100%' }}>
          <colgroup>
            {availableColumns.map((column) => (
              <col
                key={column.key}
                style={{ width: `${columnWidths[column.key]}px` }}
              />
            ))}
          </colgroup>
          <tbody>
            {(maxRows ? sortedData.slice(0, maxRows) : sortedData).map((item, sortedRowIndex) => {
              // Find the original index of this item in the unsorted data
              const originalIndex = data.findIndex(originalItem => {
                // Use Symbol as primary identifier, fall back to other unique fields
                if (item.Symbol && originalItem.Symbol) {
                  return item.Symbol === originalItem.Symbol;
                }
                if (item.symbol && originalItem.symbol) {
                  return item.symbol === originalItem.symbol;
                }
                if (item.id && originalItem.id) {
                  return item.id === originalItem.id;
                }
                // Fallback to object equality check
                return JSON.stringify(item) === JSON.stringify(originalItem);
              });

              // Create stable row identifier using the provided function or fallback
              const rowId = generateRowId 
                ? generateRowId(item, originalIndex)
                : (item.Symbol || item.symbol || item.id || `row-${originalIndex}`);
              const isRowExpanded = expandedRows.has(rowId);
              const isHiddenRow = typeof hiddenPredicate === 'function' && hiddenPredicate(item);

              return (
                <React.Fragment key={rowId}>
                  <tr 
                    onClick={
                      expandableRows 
                        ? () => toggleRowExpansion(item, originalIndex) 
                        : (onRowClick ? () => onRowClick(item, originalIndex) : undefined)
                    }
                    style={{ ...(expandableRows || onRowClick) ? { cursor: 'pointer' } : {}, ...(isHiddenRow ? { opacity: 0.55 } : {}) }}
                    className={`${isRowExpanded ? 'table-active' : ''} ${isHiddenRow ? 'dimmed-row' : ''}`}
                  >
                    {availableColumns.map((column) => {
                      // Determine which field to use from the data item
                      const dataKey = column.dataKey || column.key;
                      const value = column.dataKey === null ? null : (item[dataKey] !== undefined ? item[dataKey] : item[column.key]);
                      // ...existing code for rendering cells...
                      const renderCellValue = (value) => {
                        let formattedValue = '';
                        let tooltipText = '';

                        // Handle null/undefined
                        if (value === null || value === undefined) return '';

                        // Handle arrays by joining with commas
                        if (Array.isArray(value)) {
                          formattedValue = value.join(', ');
                          tooltipText = formattedValue;
                          return <span title={tooltipText}>{formattedValue}</span>;
                        }

                        // Handle objects by displaying them as JSON
                        if (typeof value === 'object' && value !== null) {
                          try {
                            formattedValue = JSON.stringify(value);
                            tooltipText = formattedValue;
                          } catch (e) {
                            formattedValue = '[Object]';
                            tooltipText = '[Object]';
                          }
                          return <span title={tooltipText}>{formattedValue}</span>;
                        }

                        // Format number values
                        if (typeof value === 'number') {
                          // Look for monetary fields based on column key
                          if (
                            column.key.toLowerCase().includes('value') ||
                            column.key.toLowerCase().includes('price') ||
                            column.key.toLowerCase().includes('goal') ||
                            column.key.toLowerCase().includes('dollar') ||
                            column.key.toLowerCase().includes('cost')
                          ) {
                            formattedValue = new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD'
                            }).format(value);
                            tooltipText = formattedValue;
                            return <span title={tooltipText}>{formattedValue}</span>;
                          }

                          // Look for percentage fields based on column key
                          if (
                            column.key.toLowerCase().includes('weight') ||
                            column.key.toLowerCase().includes('percent') ||
                            column.key.toLowerCase().includes('allocation')
                          ) {
                            formattedValue = `${value.toFixed(2)}%`;
                            tooltipText = `${value}%`;
                            return <span title={tooltipText}>{formattedValue}</span>;
                          }

                          // Format other numbers with 2 decimal places
                          if (Number.isFinite(value) && !Number.isInteger(value)) {
                            formattedValue = value.toFixed(2);
                            tooltipText = value.toString();
                            return <span title={tooltipText}>{formattedValue}</span>;
                          }

                          // Other integer values
                          formattedValue = value;
                          tooltipText = value.toString();
                          return <span title={tooltipText}>{formattedValue}</span>;
                        }

                        // Return string values with tooltip
                        formattedValue = String(value);
                        tooltipText = formattedValue;
                        return <span title={tooltipText}>{formattedValue}</span>;
                      };

                      // Determine cell styling for weight comparison
                      let cellStyle = { width: `${columnWidths[column.key]}px` };
                      let weightComparisonClass = '';
                      let tooltipText = '';

                      // Color coding for weight comparison - handle both sleeve table and reports table column naming
                      const hasTargetWeight = item.targetWeight !== undefined || item['Target Weight'] !== undefined;
                      if (hasTargetWeight) {
                        const targetWeight = item.targetWeight !== undefined ? item.targetWeight : item['Target Weight'];

                        // Handle reports table column naming (spaces)
                        if (column.key === 'Actual Weight' && item['Actual Weight'] !== undefined) {

                          // Use min/max target weights as thresholds for assigned weight
                          const minTargetWeight = item['Minimum Target Weight'] || item['minimumTargetWeight'] || targetWeight;
                          const maxTargetWeight = item['Maximum Target Weight'] || item['maximumTargetWeight'] || targetWeight;
                          const actualValue = item['Actual Weight'];

                          if (actualValue > maxTargetWeight) {
                            weightComparisonClass = 'text-sell fw-bold'; // Green if above maximum threshold
                            tooltipText = `Above maximum target (${maxTargetWeight}%) by ${(actualValue - maxTargetWeight).toFixed(2)}%`;
                          } else if (actualValue < minTargetWeight) {
                            weightComparisonClass = 'text-buy fw-bold'; // Red if below minimum threshold
                            tooltipText = `Below minimum target (${minTargetWeight}%) by ${(minTargetWeight - actualValue).toFixed(2)}%`;
                          } else {
                            tooltipText = `Within target range (${minTargetWeight}% - ${maxTargetWeight}%)`;
                          }
                        }

                        else if (column.key === 'Actual Weight (of Assigned)' && item['Actual Weight (of Assigned)'] !== undefined) {
                          // Use min/max target weights as thresholds for assigned weight
                          const minTargetWeight = item['Minimum Target Weight'] || item['minimumTargetWeight'] || targetWeight;
                          const maxTargetWeight = item['Maximum Target Weight'] || item['maximumTargetWeight'] || targetWeight;
                          const actualValue = item['Actual Weight (of Assigned)'];

                          if (actualValue > maxTargetWeight) {
                            weightComparisonClass = 'text-sell fw-bold'; // Green if above maximum threshold
                            tooltipText = `Above maximum target (${maxTargetWeight}%) by ${(actualValue - maxTargetWeight).toFixed(2)}%`;
                          } else if (actualValue < minTargetWeight) {
                            weightComparisonClass = 'text-buy fw-bold'; // Red if below minimum threshold
                            tooltipText = `Below minimum target (${minTargetWeight}%) by ${(minTargetWeight - actualValue).toFixed(2)}%`;
                          } else {
                            tooltipText = `Within target range (${minTargetWeight}% - ${maxTargetWeight}%)`;
                          }
                        }
                        else if (column.key === 'targetWeight' || column.key === 'Target Weight') {
                          // Highlight target weight for reference
                          weightComparisonClass = 'fw-bold';
                          tooltipText = 'Target allocation';
                        }
                      }

                      // Create a function to render the cell content with weight difference
                      const renderWeightWithDifference = () => {
                        // Check for both camelCase keys (sleeve table) and space-separated keys (reports table)
                        if ((column.key !== 'Actual Weight' && column.key !== 'Actual Weight (of Assigned)') ||
                          (item.targetWeight === undefined && item['Target Weight'] === undefined)) {
                          return renderCellValue(value);
                        }

                        // Get the target weight value (works for both tables)
                        const targetWeight = item.targetWeight !== undefined ? item.targetWeight : item['Target Weight'];

                        // Handle different logic for Actual Weight vs Actual Weight (of Assigned)
                        if (column.key === 'Actual Weight') {
                          // For assigned weight, always calculate difference from target but only highlight when outside min/max
                          const minTargetWeight = item['Minimum Target Weight'] || item['minimumTargetWeight'] || targetWeight;
                          const maxTargetWeight = item['Maximum Target Weight'] || item['maximumTargetWeight'] || targetWeight;
                          const actualValue = item['Actual Weight'];
                          const formattedValue = renderCellValue(value);

                          // Always calculate difference from target
                          const difference = actualValue - targetWeight;

                          let tooltipText = '';
                          if (actualValue > maxTargetWeight) {
                            tooltipText = `Value: ${actualValue}%. Above maximum target (${maxTargetWeight}%)`;
                            return (
                              <span title={tooltipText}>
                                {formattedValue} <span className="text-sell">({difference > 0 ? '+' : ''}${difference.toFixed(2)}%)</span>
                              </span>
                            );
                          } else if (actualValue < minTargetWeight) {
                            tooltipText = `Value: ${actualValue}%. Below minimum target (${minTargetWeight}%)`;
                            return (
                              <span title={tooltipText}>
                                {formattedValue} <span className="text-buy">({difference > 0 ? '+' : ''}${difference.toFixed(2)}%)</span>
                              </span>
                            );
                          } else {
                            // Within acceptable range - show difference from target without color coding
                            tooltipText = `Value: ${actualValue}%. Within target range (${minTargetWeight}% - ${maxTargetWeight}%). Difference from target: ${difference > 0 ? '+' : ''}${difference.toFixed(2)}%`;
                            return (
                              <span title={tooltipText}>
                                {formattedValue} <span>({difference > 0 ? '+' : ''}${difference.toFixed(2)}%)</span>
                              </span>
                            );
                          }
                        }

                        else if (column.key === 'Actual Weight (of Assigned)') {
                          // For assigned weight, always calculate difference from target but only highlight when outside min/max
                          const minTargetWeight = item['Minimum Target Weight'] || item['minimumTargetWeight'] || targetWeight;
                          const maxTargetWeight = item['Maximum Target Weight'] || item['maximumTargetWeight'] || targetWeight;
                          const actualValue = item['Actual Weight (of Assigned)'];
                          const formattedValue = renderCellValue(value);

                          // Always calculate difference from target
                          const difference = actualValue - targetWeight;

                          let tooltipText = '';
                          if (actualValue > maxTargetWeight) {
                            tooltipText = `Value: ${actualValue}%. Above maximum target (${maxTargetWeight}%)`;
                            return (
                              <span title={tooltipText}>
                                {formattedValue} <span className="text-sell">({difference > 0 ? '+' : ''}${difference.toFixed(2)}%)</span>
                              </span>
                            );
                          } else if (actualValue < minTargetWeight) {
                            tooltipText = `Value: ${actualValue}%. Below minimum target (${minTargetWeight}%)`;
                            return (
                              <span title={tooltipText}>
                                {formattedValue} <span className="text-buy">({difference > 0 ? '+' : ''}${difference.toFixed(2)}%)</span>
                              </span>
                            );
                          } else {
                            // Within acceptable range - show difference from target without color coding
                            tooltipText = `Value: ${actualValue}%. Within target range (${minTargetWeight}% - ${maxTargetWeight}%). Difference from target: ${difference > 0 ? '+' : ''}${difference.toFixed(2)}%`;
                            return (
                              <span title={tooltipText}>
                                {formattedValue} <span>({difference > 0 ? '+' : ''}${difference.toFixed(2)}%)</span>
                              </span>
                            );
                          }
                        }

                        return renderCellValue(value);
                      };

                      return (
                        <td
                          key={`${originalIndex}-${column.key}`}
                          style={cellStyle}
                          className={weightComparisonClass}
                          title={tooltipText} // Add tooltip explanation for the color
                        >{customRender.renderCell
                          ? customRender.renderCell(value, item, column.key)
                          : customRender[column.key]
                            ? customRender[column.key](value, item, column.key)
                            : (column.key === 'Actual Weight' || column.key === 'Actual Weight (of Assigned)') &&
                              (item.targetWeight !== undefined || item['Target Weight'] !== undefined)
                              ? renderWeightWithDifference()
                              : column.format
                                ? column.format(value, item)
                                : columnClickHandlers[column.key]
                                  ? (
                                    <span
                                      style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        columnClickHandlers[column.key](item, originalIndex);
                                      }}
                                    >
                                      {renderCellValue(value)}
                                    </span>
                                  )
                                  : renderCellValue(value)
                          }
                        </td>
                      );
                    })}
                  </tr>
                  {/* Render expanded row directly below parent if this row is expanded */}
                  {expandableRows && isRowExpanded && (
                    <tr key={`expanded-${rowId}`} className="expanded-row">
                      <td colSpan={availableColumns.length}>
                        {renderExpandedContent ? renderExpandedContent(item, rowId) : <pre>{JSON.stringify(item, null, 2)}</pre>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {/* Show row limit indicator if data is truncated */}
            {maxRows && data.length > maxRows && (
              <tr>
                <td colSpan={availableColumns.length} className="text-center text-muted py-2 row-limit-indicator">
                  Showing {maxRows} of {data.length} rows. Use filters to find specific items.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default React.memo(SortableResizableTable);
